/**
 * Roots whose visitors are guests holding a link, never signed-in users.
 *
 * No trailing slashes, on purpose: the match below reads each as "this exact path,
 * or anything beneath it". Mixing bare and slash-terminated entries meant `/ks` and
 * `/tidsplan` were not recognised and still got the login screen — the very dead end
 * this function exists to remove.
 *
 * `/portal-old` deliberately does NOT match `/portal`: only a whole segment counts,
 * so a future non-guest route sharing a prefix cannot accidentally shed its auth.
 *
 * Lives here rather than in App.tsx because NetClientsProvider needs it too (the
 * company lock must not cover a guest's page), and App.tsx imports the provider's
 * context — the other way round would be a cycle.
 */
const GUEST_PATH_ROOTS = [
  '/portal',
  '/recipients',
  '/tidsplan',
  '/ks',
  // Each of these reads a `?token=` and identifies its visitor from that alone: a
  // customer accepting or rejecting a quote from the emailed link, an invitee
  // accepting a partner invitation, and the two pages a guest uses to complete
  // defects. They sit inside AuthenticatedRoutes like the rest, so until they were
  // listed here a customer's quote link answered with a login form for an account
  // they do not have.
  //
  // /convert-partner is deliberately absent: it reads no token and converts the
  // signed-in partner's own account, so it genuinely requires a session.
  '/quotes/respond',
  '/invitations/accept',
  '/defect/complete',
  '/defect/batch',
] as const;

export function isGuestPath(pathname: string): boolean {
  // React Router gives the path alone — no query, no hash — but a trailing slash
  // does occur (`/portal/`), so normalise before comparing.
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;

  return GUEST_PATH_ROOTS.some(root => path === root || path.startsWith(`${root}/`));
}
